import { useEffect, useState } from "react";
import { navigate, useRoute, type Route } from "./lib/router";
import type { Me } from "./types";
import { TopBar } from "./components/TopBar";
import { Intro } from "./components/Intro";
import { ScannerPage } from "./pages/ScannerPage";
import { MarketPage } from "./pages/MarketPage";
import { GrabitPage } from "./pages/GrabitPage";
import { UsPage } from "./pages/UsPage";
import { UsMarketPage } from "./pages/UsMarketPage";
import { UsTradesPage } from "./pages/UsTradesPage";
import { UsBacktestPage } from "./pages/UsBacktestPage";
import { LoginPage } from "./pages/LoginPage";
import { AccountPage } from "./pages/AccountPage";
import { JournalPage } from "./pages/JournalPage";
import { BacktestPage } from "./pages/BacktestPage";
import { ActiveTradesPage } from "./pages/ActiveTradesPage";
import { SettingsPage } from "./pages/SettingsPage";
import { auth, useAuth } from "./lib/auth";
import { isGrabitRoute, isUsRoute, linkHandler } from "./lib/router";

/** Интро — один раз за сессию браузера; ?intro в адресе — показать снова. */
function shouldShowIntro(): boolean {
  if (new URLSearchParams(window.location.search).has("intro")) return true;
  try {
    return sessionStorage.getItem("insidera-intro") !== "seen";
  } catch {
    return true;
  }
}

function markIntroSeen() {
  try {
    sessionStorage.setItem("insidera-intro", "seen");
  } catch {
    /* без сохранения — покажется ещё раз */
  }
}

export function App() {
  const route = useRoute();
  const me = useAuth();
  const [intro, setIntro] = useState(shouldShowIntro);

  useEffect(() => {
    if (window.location.pathname === "/") navigate("/ru");
  }, []);

  useEffect(() => {
    document.title =
      route.name === "market"
        ? `${route.code} — ВнутриCOT`
          : route.name === "journal"
            ? "Журнал сигналов — ВнутриCOT"
            : route.name === "backtest"
              ? "Бэктест — ВнутриCOT"
            : route.name === "trades"
              ? "Активные сделки — ВнутриCOT"
            : route.name === "settings"
              ? "Настройки — ВнутриCOT"
        : route.name === "usTrades"
          ? "Сделки США — ВнутриCOT"
        : route.name === "usBacktest"
          ? "Бэктест США — ВнутриCOT"
        : route.name === "us"
          ? "США — ВнутриCOT"
        : route.name === "usMarket"
          ? `${route.code} · США — ВнутриCOT`
          : route.name === "grabit"
            ? "DeMark · FUTOI — ВнутриCOT"
          : route.name === "login"
            ? "Вход — ВнутриCOT"
            : route.name === "account"
              ? "Аккаунт — ВнутриCOT"
              : "ВнутриCOT — позиции крупных игроков";
  }, [route]);

  return (
    <>
      {intro && (
        <Intro
          onComplete={() => {
            markIntroSeen();
            setIntro(false);
          }}
        />
      )}
      <TopBar route={route} />
      <Routes route={route} me={me} />
      <footer className="footer">
        {isGrabitRoute(route) ? (
          <p>
            Методика — Т. ДеМарк, «The New Science of Technical Analysis», гл. 9 «Daily Range Projections». Данные —
            Мосбиржа: ISS (дневные свечи) и APIM (открытые позиции FUTOI, шаг 5 минут). Прогноз строится по OHLC
            предыдущего торгового дня. Не является инвестиционной рекомендацией.
          </p>
        ) : (
          <p>
            Методика — Л. Уильямс, «Trade Stocks and Commodities with the Insiders». Данные —{" "}
            {isUsRoute(route)
              ? "CFTC и ICE Futures Europe (отчёты о позициях), Yahoo Finance (цены, прототип)."
              : "Мосбиржа (ISS), итоги прошлого торгового дня."}
            Не является инвестиционной рекомендацией.
          </p>
        )}
      </footer>
    </>
  );
}

function Routes({ route, me }: { route: Route; me: Me | undefined }) {
  // Вошёл, уже вошёл — со страницы входа уводим; временный пароль — только аккаунт.
  useEffect(() => {
    if (!me) return;
    if (me.authenticated && route.name === "login") navigate("/ru");
    else if (me.authenticated && me.mustChangePassword && route.name !== "account") navigate("/account");
    else if (!me.authenticated && route.name === "account") navigate("/login?next=/account");
  }, [me, route]);

  if (!me) return <main className="page"><div className="skeleton" aria-busy="true">Загружаем…</div></main>;
  if (route.name === "login") return <LoginPage />;
  if (route.name === "account") return me.authenticated ? <AccountPage me={me} /> : null;
  if (me.authenticated && me.mustChangePassword) return null;
  if (route.name === "us") return me.authenticated ? <UsPage /> : <Locked code="us" />;
  if (route.name === "usTrades") return me.authenticated ? <UsTradesPage /> : <Locked code="us" />;
  if (route.name === "usBacktest") return me.authenticated ? <UsBacktestPage /> : <Locked code="us" />;
  if (route.name === "usMarket") return me.authenticated ? <UsMarketPage key={route.code} code={route.code} /> : <Locked code="us" />;
  if (route.name === "grabit") return <GrabitPage signedIn={me.authenticated} />;
  if (route.name === "scanner") return <ScannerPage demo={!me.authenticated} />;
  if (route.name === "settings") return <SettingsPage />;
  if (route.name === "journal") return me.authenticated ? <JournalPage /> : <Locked code="journal" />;
  if (route.name === "backtest") return me.authenticated ? <BacktestPage /> : <Locked code="backtest" />;
  if (route.name === "trades") return me.authenticated ? <ActiveTradesPage /> : <Locked code="trades" />;
  if (me.authenticated) return <MarketPage key={route.code} code={route.code} demo={false} />;
  if (auth.isDemoMarket(route.code)) return <MarketPage key={route.code} code={route.code} demo />;
  return <Locked code={route.code} />;
}

function Locked({ code }: { code: string }) {
  const to = `/login?next=${encodeURIComponent(code === "us" ? "/us" : `/ru/${code}`)}`;
  return (
    <main className="page auth-page">
      <div className="auth-card">
        <h1>{code === "us" ? "Рынок США" : code}: после входа</h1>
        <p className="muted">
          В демо-версии открыты {auth.demoMarkets().join(", ") || "несколько инструментов"}. Чтобы смотреть этот
          инструмент, войдите.
        </p>
        <a className="btn btn-primary" href={to} onClick={linkHandler(to)}>
          Войти
        </a>
        <a className="back" href="/ru" onClick={linkHandler("/ru")}>
          ← К демо-сканеру
        </a>
      </div>
    </main>
  );
}
